import _extends from '@babel/runtime/helpers/extends';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _jsx from '@babel/runtime/helpers/jsx';
import React, { forwardRef, useRef, useState, useEffect, cloneElement } from 'react';
import classNames from 'classnames';
import ProgressIndicatorCircular from '@andes/progress-indicator-circular';
import { VisuallyHidden } from '@andes/common';
import warning from '@andes/common/build/utils/warning';
import { useId, getPatchedI18n, AndesContext } from '@andes/context';
import { FloatingMenu, FloatingMenuItem } from '@andes/floating-menu';
import transformToKebabCase from '@andes/common/build/utils/transformToKebabCase';
const namespace$2 = 'andes-button';
const namespaceDropdownButton = 'andes-button-dropdown';
const _excluded$3 = ["children", "className", "disabled", "fullWidth", "hierarchy", "href", "id", "innerRef", "isFetching", "isFetchingComplete", "loading", "onClick", "size", "spinner", "spinnerLabel", "srAnnouncement", "srLabel", "type"];
const progressIndicatorSize = {
  small: 'xsmall',
  medium: 'xsmall',
  large: 'small'
};
const ButtonText = _ref => {
  let {
    children
  } = _ref;
  return /*#__PURE__*/_jsx("span", {
    className: "".concat(namespace$2, "__text")
  }, void 0, children);
};
const Button = /*#__PURE__*/function () {
  const Button = /*#__PURE__*/forwardRef((props, ref) => {
    const {
        children,
        className,
        disabled = false,
        fullWidth = false,
        hierarchy = 'loud',
        href,
        id,
        innerRef: _willdeprecate$innerRef,
        isFetching: _willdeprecate$isFetching,
        isFetchingComplete: _willdeprecate$isFetchingComplete,
        loading,
        onClick,
        size = 'large',
        spinner: _willdeprecate$spinner,
        spinnerLabel: _willdeprecate$spinnerLabel,
        srAnnouncement,
        srLabel,
        type = 'button'
      } = props,
      otherProps = _objectWithoutProperties(props, _excluded$3);
    const componentId = useId(id);
    const computedLoading = loading || _willdeprecate$isFetching;
    const srLabelTimeout = useRef();
    const firstRender = useRef(true);
    const [srAnnouncementLabel, setSrAnnouncementLabel] = useState('');
    const [loadingComplete, setLoadingComplete] = useState(false);
    useEffect(() => {
      if (!computedLoading) {
        setSrAnnouncementLabel('');
        if (!firstRender.current) setLoadingComplete(true);
      }
      if (firstRender) firstRender.current = false;
    }, [computedLoading]);
    useEffect(() => {
      let finishTimeout;
      if (loadingComplete) {
        finishTimeout = setTimeout(() => {
          setLoadingComplete(false);
        }, 100);
      }
      return () => clearTimeout(finishTimeout);
    }, [loadingComplete]);
    useEffect(() => () => {
      clearTimeout(srLabelTimeout.current);
    }, []);
    warning(!computedLoading || computedLoading && (srAnnouncement || _willdeprecate$spinnerLabel), "<ButtonWithSpinner> needs some plain text contents for accessibility reasons.\n    Please add a 'srAnnouncement' prop.");
    const classes = classNames(namespace$2, className, {
      ["".concat(namespace$2, "--").concat(size)]: size,
      ["".concat(namespace$2, "--").concat(hierarchy)]: hierarchy,
      ["".concat(namespace$2, "--disabled")]: disabled,
      ["".concat(namespace$2, "--full-width")]: fullWidth,
      ["".concat(namespace$2, "--loading")]: computedLoading,
      ["".concat(namespace$2, "--loading-complete")]: loadingComplete
    });
    const sharedProps = _objectSpread({
      className: classes,
      id: componentId,
      'aria-label': srLabel
    }, otherProps);
    const content = /*#__PURE__*/_jsx("span", {
      className: "".concat(namespace$2, "__content")
    }, void 0, children);
    if (href) {
      const anchorProps = _objectSpread(_objectSpread({}, disabled || computedLoading ? {
        role: 'link',
        'aria-disabled': true
      } : {
        href
      }), {}, {
        ref: ref || _willdeprecate$innerRef,
        onClick: onClick
      }, sharedProps);
      return /*#__PURE__*/React.createElement("a", anchorProps, content);
    }
    const buttonProps = _objectSpread({
      disabled: disabled || computedLoading,
      ref: ref || _willdeprecate$innerRef,
      type: type
    }, sharedProps);
    const customOnClick = event => {
      const label = srAnnouncement || _willdeprecate$spinnerLabel || '';
      srLabelTimeout.current = setTimeout(() => setSrAnnouncementLabel(label), 30);
      if (typeof onClick === 'function') onClick(event);
    };
    const progressSize = progressIndicatorSize[size];
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", _extends({}, buttonProps, {
      onClick: customOnClick
    }), content, computedLoading && /*#__PURE__*/_jsx(ProgressIndicatorCircular, {
      size: progressSize,
      color: hierarchy === 'loud' ? 'white' : 'blue'
    })), computedLoading && /*#__PURE__*/_jsx(VisuallyHidden, {
      "aria-live": "assertive"
    }, void 0, srAnnouncementLabel));
  });
  Button.displayName = 'Button';
  return Button;
}();
const _excluded$2 = ["className", "id", "listContent", "namespace", "onClickOutside", "placement", "popperOptions", "srLabel", "trigger", "visible"];
const ButtonListContent = _ref => {
  let {
      className,
      id,
      listContent,
      namespace,
      onClickOutside,
      placement,
      popperOptions,
      srLabel,
      trigger,
      visible
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded$2);
  return /*#__PURE__*/React.createElement(FloatingMenu, _extends({
    className: className,
    placement: placement,
    open: visible,
    popperOptions: popperOptions,
    trigger: trigger,
    id: id,
    srLabel: srLabel
  }, props), listContent.map((_ref2, i) => {
    let {
      title,
      description,
      href,
      onClick
    } = _ref2;
    return /*#__PURE__*/_jsx(FloatingMenuItem, {
      id: "".concat(namespace, "__menu-item-").concat(i),
      href: href,
      className: "".concat(namespace, "__menu-item"),
      value: title,
      title: title,
      description: description,
      onClick: onClick
    }, title);
  }));
};
var ButtonListContent$1 = ButtonListContent;
var module$2 = "@andes/button-split";
var locale$2 = "en-US";
var translations$2 = {
  "andes-button-split\u0004menú desplegable": [null, "Dropdown menu"]
};
var enTranslations = {
  module: module$2,
  locale: locale$2,
  translations: translations$2
};
var module$1 = "@andes/button-split";
var locale$1 = "es-AR";
var translations$1 = {
  "andes-button-split\u0004menú desplegable": [null, "Menú desplegable"]
};
var esTranslations = {
  module: module$1,
  locale: locale$1,
  translations: translations$1
};
var module = "@andes/button-split";
var locale = "pt-BR";
var translations = {
  "andes-button-split\u0004menú desplegable": [null, "Menu suspenso"]
};
var ptTranslations = {
  module: module,
  locale: locale,
  translations: translations
};
var _ChevronDown$1, _ChevronUP$1;
const ChevronDown$1 = () => /*#__PURE__*/_jsx("svg", {
  width: "20px",
  height: "20px",
  viewBox: "0 0 20 20",
  version: "1.1",
  xmlns: "http://www.w3.org/2000/svg"
}, void 0, /*#__PURE__*/_jsx("g", {
  stroke: "none",
  strokeWidth: "1",
  fill: "none",
  fillRule: "evenodd",
  fillOpacity: "0.8"
}, void 0, /*#__PURE__*/_jsx("polygon", {
  className: "".concat(namespaceDropdownButton, "__svg-fill"),
  fillRule: "nonzero",
  transform: "translate(10.000000, 10.000000) rotate(-90.000000) translate(-10.000000, -10.000000) ",
  points: "8.4482158 9.99750228 13.243845 5.20187305 12.3953169 4.35334491 6.75115953 9.99750228 12.4003123 15.6466551 13.2488405 14.798127"
})));
const ChevronUP$1 = () => /*#__PURE__*/_jsx("svg", {
  width: "20px",
  height: "20px",
  viewBox: "0 0 20 20",
  version: "1.1",
  xmlns: "http://www.w3.org/2000/svg"
}, void 0, /*#__PURE__*/_jsx("g", {
  stroke: "none",
  strokeWidth: "1",
  fill: "none",
  fillRule: "evenodd",
  fillOpacity: "0.8"
}, void 0, /*#__PURE__*/_jsx("polygon", {
  className: "".concat(namespaceDropdownButton, "__svg-fill"),
  fillRule: "nonzero",
  transform: "translate(10.001873, 9.995000) rotate(90.000000) translate(-10.001873, -9.995000) ",
  points: "8.45008885 9.99250228 13.2457181 5.19687305 12.3971899 4.34834491 6.75303257 9.99250228 12.4021854 15.6416551 13.2507135 14.793127"
})));
const DropdownButton = /*#__PURE__*/function () {
  const DropdownButton = /*#__PURE__*/forwardRef((_ref, ref) => {
    let {
      ariaLabel: _willdeprecate$ariaLabel,
      buttonText: _willdeprecate$buttonText,
      appendTo,
      className,
      children,
      disabled = false,
      fullWidth = false,
      hierarchy = 'loud',
      id,
      listContent,
      menuWidth,
      side = 'bottom',
      size = 'large',
      srLabel
    } = _ref;
    const componentId = useId(id);
    const [showOptions, setShowOptions] = useState(false);
    const i18n = getPatchedI18n(AndesContext, enTranslations, esTranslations, ptTranslations);
    const handleOpen = () => {
      setShowOptions(true);
    };
    const handleClose = () => {
      setShowOptions(false);
    };
    const classes = classNames(namespaceDropdownButton, className);
    const listClasses = classNames("".concat(namespaceDropdownButton, "__menu"), className, {
      ["".concat(namespaceDropdownButton, "__menu--").concat(side)]: side
    });
    const triggerChildren = children || _willdeprecate$buttonText;
    const trigger = /*#__PURE__*/_jsx(Button, {
      className: classes,
      hierarchy: hierarchy,
      size: size,
      "aria-expanded": showOptions,
      srLabel: srLabel || _willdeprecate$ariaLabel,
      disabled: disabled,
      fullWidth: fullWidth,
      id: componentId
    }, void 0, triggerChildren && /*#__PURE__*/_jsx(ButtonText, {}, void 0, triggerChildren), !showOptions ? _ChevronDown$1 || (_ChevronDown$1 = /*#__PURE__*/_jsx(ChevronDown$1, {})) : _ChevronUP$1 || (_ChevronUP$1 = /*#__PURE__*/_jsx(ChevronUP$1, {})));
    return /*#__PURE__*/React.createElement("div", {
      ref: ref
    }, /*#__PURE__*/_jsx(ButtonListContent$1, {
      appendTo: appendTo,
      placement: side,
      listContent: listContent,
      onOpen: handleOpen,
      onClose: handleClose,
      popperOptions: {
        modifiers: [{
          name: 'sameWidth',
          enabled: !menuWidth,
          fn: _ref2 => {
            let {
              state
            } = _ref2;
            state.styles.popper.width = "".concat(state.rects.reference.width, "px");
          },
          phase: 'beforeWrite',
          requires: ['computeStyles'],
          effect: _ref3 => {
            let {
              state
            } = _ref3;
            state.elements.popper.style.width = "".concat(state.elements.reference.clientWidth, "px");
          }
        }]
      },
      trigger: trigger,
      namespace: namespaceDropdownButton,
      id: "".concat(componentId, "-menu"),
      className: listClasses,
      srLabel: srLabel || _willdeprecate$ariaLabel || i18n.pgettext(namespaceDropdownButton, 'menú desplegable'),
      width: menuWidth || '100%'
    }));
  });
  DropdownButton.displayName = 'DropdownButton';
  return DropdownButton;
}();
var DropdownButton$1 = DropdownButton;
var _svg, _svg2, _ChevronDown, _ChevronUP;
const namespace$1 = 'andes-button-split';
const ChevronDown = () => _svg || (_svg = /*#__PURE__*/_jsx("svg", {
  width: "20px",
  height: "20px",
  viewBox: "0 0 20 20",
  version: "1.1",
  xmlns: "http://www.w3.org/2000/svg"
}, void 0, /*#__PURE__*/_jsx("title", {}, void 0, "Abrir men\xFA de opciones / Mostrar opciones"), /*#__PURE__*/_jsx("g", {
  stroke: "none",
  strokeWidth: "1",
  fill: "none",
  fillRule: "evenodd",
  fillOpacity: "0.8"
}, void 0, /*#__PURE__*/_jsx("polygon", {
  className: "".concat(namespace$1, "__svg-fill"),
  fillRule: "nonzero",
  transform: "translate(10.000000, 10.000000) rotate(-90.000000) translate(-10.000000, -10.000000) ",
  points: "8.4482158 9.99750228 13.243845 5.20187305 12.3953169 4.35334491 6.75115953 9.99750228 12.4003123 15.6466551 13.2488405 14.798127"
}))));
const ChevronUP = () => _svg2 || (_svg2 = /*#__PURE__*/_jsx("svg", {
  width: "20px",
  height: "20px",
  viewBox: "0 0 20 20",
  version: "1.1",
  xmlns: "http://www.w3.org/2000/svg"
}, void 0, /*#__PURE__*/_jsx("title", {}, void 0, "Cerrar men\xFA de opciones / Ocultar opciones"), /*#__PURE__*/_jsx("g", {
  stroke: "none",
  strokeWidth: "1",
  fill: "none",
  fillRule: "evenodd",
  fillOpacity: "0.8"
}, void 0, /*#__PURE__*/_jsx("polygon", {
  className: "".concat(namespace$1, "__svg-fill"),
  fillRule: "nonzero",
  transform: "translate(10.001873, 9.995000) rotate(90.000000) translate(-10.001873, -9.995000) ",
  points: "8.45008885 9.99250228 13.2457181 5.19687305 12.3971899 4.34834491 6.75303257 9.99250228 12.4021854 15.6416551 13.2507135 14.793127"
}))));
const placement = {
  top: 'top-start',
  bottom: 'bottom-start',
  bottomRight: 'bottom-start',
  topRight: 'top-start'
};
const SplitButton = /*#__PURE__*/function () {
  const SplitButton = /*#__PURE__*/forwardRef((_ref, ref) => {
    let {
      buttonText: _willdeprecate$buttonText,
      children,
      className,
      hierarchy = 'loud',
      id,
      listContent,
      onClick,
      side = 'bottomRight',
      srLabel
    } = _ref;
    const componentId = useId(id);
    const [showOptions, setShowOptions] = useState(false);
    const show = () => {
      setShowOptions(true);
    };
    const hide = () => {
      setShowOptions(false);
    };
    const classes = classNames(namespace$1, className);
    const popperOptions = side === 'top' || side === 'bottom' ? {
      modifiers: [{
        name: 'sameWidth',
        enabled: true,
        fn: _ref2 => {
          let {
            state
          } = _ref2;
          state.styles.popper.left = "-".concat(state.elements.reference.parentElement.offsetLeft, "px");
          state.styles.popper.width = "".concat(state.elements.reference.parentElement.offsetLeft + state.rects.reference.width, "px");
        },
        phase: 'beforeWrite',
        requires: ['computeStyles'],
        effect: _ref3 => {
          let {
            state
          } = _ref3;
          state.elements.popper.style.width = "".concat(state.elements.reference.parentElement.offsetLeft + state.elements.reference.clientWidth, "px");
        }
      }]
    } : undefined;
    const listClasses = classNames("".concat(namespace$1, "__menu"), className, {
      ["".concat(namespace$1, "__menu--").concat(transformToKebabCase(side))]: side
    });
    const trigger = i18n => /*#__PURE__*/_jsx(Button, {
      className: "".concat(namespace$1, "-chevron"),
      hierarchy: hierarchy,
      "aria-expanded": showOptions,
      srLabel: srLabel || i18n.pgettext(namespace$1, 'menú desplegable'),
      id: "".concat(componentId, "-chevron")
    }, void 0, !showOptions ? _ChevronDown || (_ChevronDown = /*#__PURE__*/_jsx(ChevronDown, {})) : _ChevronUP || (_ChevronUP = /*#__PURE__*/_jsx(ChevronUP, {})));
    const i18n = getPatchedI18n(AndesContext, enTranslations, esTranslations, ptTranslations);
    const buttonListContentPlacement = placement["".concat(side)];
    return /*#__PURE__*/React.createElement("div", {
      className: classes,
      ref: ref
    }, /*#__PURE__*/_jsx(Button, {
      className: "".concat(namespace$1, "-text"),
      onClick: onClick,
      hierarchy: hierarchy,
      id: "".concat(componentId, "-text")
    }, void 0, /*#__PURE__*/_jsx(ButtonText, {}, void 0, children || _willdeprecate$buttonText)), /*#__PURE__*/_jsx(ButtonListContent$1, {
      placement: buttonListContentPlacement,
      listContent: listContent,
      visible: showOptions,
      onClickOutside: hide,
      onOpen: show,
      onClose: hide,
      popperOptions: popperOptions,
      trigger: trigger(i18n),
      namespace: namespace$1,
      id: "".concat(componentId, "-menu"),
      srLabel: srLabel || i18n.pgettext(namespace$1, 'menú desplegable'),
      className: listClasses,
      width: side === 'top' || side === 'bottom' ? '100%' : 'max-content'
    }));
  });
  SplitButton.displayName = 'SplitButton';
  return SplitButton;
}();
var SplitButton$1 = SplitButton;
const _excluded$1 = ["children", "className", "disabled", "hierarchy", "id", "loading", "progressLabel", "progressSrAnnouncement", "timeout"];
const STATES = {
  LOADING: 'loading',
  ENTERED: 'entered',
  FINISHED: 'finished'
};
const DEFAULT_HIERARCHY$1 = 'loud';
const HIERARCHY_ALLOWED = {
  [DEFAULT_HIERARCHY$1]: true,
  quiet: true
};
const buildClassesObject = function (stages) {
  let defaultClasses = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return stages.reduce((classes, stage) => _objectSpread(_objectSpread({}, classes), {}, {
    [stage]: true
  }), defaultClasses);
};
const ProgressButton = /*#__PURE__*/function () {
  const ProgressButton = /*#__PURE__*/forwardRef((props, ref) => {
    const {
        children,
        className,
        disabled,
        hierarchy = 'loud',
        id,
        loading = false,
        progressLabel,
        progressSrAnnouncement,
        timeout = 1000
      } = props,
      otherProps = _objectWithoutProperties(props, _excluded$1);
    const componentId = useId(id);
    const mounted = useRef(false);
    const [stages, setStages] = useState([STATES.ENTERED]);
    const hierarchySelected = HIERARCHY_ALLOWED[hierarchy] ? hierarchy : DEFAULT_HIERARCHY$1;
    useEffect(() => {
      if (mounted.current) {
        if (loading) {
          setStages([STATES.LOADING]);
        } else {
          setStages([STATES.LOADING, STATES.FINISHED]);
          const finishTimeout = setTimeout(() => {
            setStages([STATES.FINISHED]);
          }, timeout);
          return () => clearTimeout(finishTimeout);
        }
      }
    }, [loading, timeout]);
    useEffect(() => {
      mounted.current = true;
      if (loading) {
        setStages([STATES.LOADING]);
      }
    }, []);
    const classObject = buildClassesObject(stages, {
      'andes-button--progress__label': Boolean(progressLabel)
    });
    const classes = classNames('andes-button--progress', className, classObject);
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, _extends({
      id: componentId,
      hierarchy: hierarchySelected,
      className: classes,
      ref: ref,
      disabled: disabled || loading
    }, otherProps), progressLabel && loading ? /*#__PURE__*/_jsx("span", {
      className: "andes-button--progress__content"
    }, void 0, progressLabel) : children), /*#__PURE__*/_jsx(VisuallyHidden, {
      "aria-live": "assertive"
    }, void 0, (progressSrAnnouncement || progressLabel) && loading ? progressSrAnnouncement || progressLabel : ''));
  });
  ProgressButton.displayName = 'ProgressButton';
  return ProgressButton;
}();
var ProgressButton$1 = ProgressButton;
const _excluded = ["behavior", "className", "hierarchy", "icon", "size", "srLabel", "text"];
const DEFAULT_HIERARCHY = 'loud';
const namespace = 'andes-button';
const FloatingActionButton = /*#__PURE__*/function () {
  const FloatingActionButton = /*#__PURE__*/forwardRef((props, ref) => {
    const {
        behavior = 'expanded',
        className,
        hierarchy = DEFAULT_HIERARCHY,
        icon,
        size = 'large',
        srLabel,
        text
      } = props,
      otherProps = _objectWithoutProperties(props, _excluded);
    warning(text || srLabel, "<FloatingActionButton> needs some plain text contents for accessibility reasons.\n      Please add an 'srLabel' or 'text' prop.");
    const classes = classNames("".concat(namespace, "--fab"), "".concat(namespace, "--").concat(behavior), className, {
      ["".concat(namespace, "--").concat(size)]: size
    });
    const content = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/_jsx("span", {
      className: "".concat(namespace, "--icon-container")
    }, void 0, /*#__PURE__*/cloneElement(icon, {
      'aria-hidden': true
    })), text && /*#__PURE__*/_jsx(ButtonText, {}, void 0, text));
    return /*#__PURE__*/React.createElement(Button, _extends({}, otherProps, {
      hierarchy: hierarchy,
      className: classes,
      srLabel: srLabel || text,
      ref: ref
    }), content);
  });
  FloatingActionButton.displayName = 'FloatingActionButton';
  return FloatingActionButton;
}();
var FloatingActionButton$1 = FloatingActionButton;
export { Button, ButtonText, DropdownButton$1 as DropdownButton, FloatingActionButton$1 as FloatingActionButton, ProgressButton$1 as ProgressButton, SplitButton$1 as SplitButton, Button as default };
